"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { cn } from "@/app/utils/className";

interface PinnedScrollItem {
  title: string;
  description: string;
}

interface PinnedScrollSectionProps {
  title: React.ReactNode;
  items: PinnedScrollItem[];
  contentClassName?: string;
  yOffset?: number;
  rangeBackground?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  itemClassName?: string;
  containerClassName?: string;
  maskGradient?: string;
}

export const PinnedScrollSection = ({
  title,
  items,
  contentClassName = "h-[35vh]",
  yOffset = 90,
  rangeBackground = "linear-gradient(90deg, var(--color-haizea-green) 0%, var(--color-haizea-blue) 50%, var(--color-haizea-orange) 100%)",
  titleClassName = "font-medium",
  descriptionClassName = "text-slate-600 dark:text-slate-300",
  itemClassName = "text-slate-700 dark:text-slate-200 my-4",
  containerClassName = "",
  maskGradient = "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 2%, rgba(255, 255, 255, 0.7) 5%, rgba(255, 255, 255, 1) 20%, rgba(255, 255, 255, 1) 70%, rgba(255, 255, 255, 0.5) 95%)",
}: PinnedScrollSectionProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<(HTMLParagraphElement | null)[]>([]);
  const rangeRef = useRef<HTMLInputElement | null>(null);

  // Store timeline reference for manual control
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const content = contentRef.current;
      const container = containerRef.current;
      const scrollItems = itemsRef.current.filter(Boolean);
      const range = rangeRef.current;

      if (!content || !container || !scrollItems.length || !range) return;

      // Detect mobile devices
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      // Set initial state for range input
      range.value = "0";

      // Main pinned section animation
      const mainTl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: content,
          start: "center center",
          end: () => `+=${scrollItems.length * 500}`,
          scrub: isMobile ? 0.5 : 1, // Faster scrub on mobile
          pin: container,
          anticipatePin: isMobile ? 0 : 1, // Disable anticipatePin on mobile
          pinSpacing: true,
          fastScrollEnd: isMobile ? true : false,
          onUpdate: (self) => {
            // Only update range if user isn't currently dragging it
            if (!range.matches(":active")) {
              range.value = self.progress.toString();
            }
          },
          invalidateOnRefresh: true,
        },
      });

      // Store timeline reference for manual control
      timelineRef.current = mainTl;

      // Animate items with timeline
      scrollItems.forEach((item, index) => {
        mainTl
          .fromTo(
            item,
            { y: 0 },
            {
              y: -scrollItems.length * yOffset,
              duration: 1,
              force3D: true, // Enable hardware acceleration
            },
            index * 0.25
          )
          .to(
            item,
            {
              opacity: 1,
              yoyo: true,
              duration: 1,
              force3D: true,
            },
            "<0.25"
          );
      });

      // Range input interaction - control the timeline progress
      const handleRangeChange = () => {
        const value = parseFloat(range.value);

        // Use timeline's progress method to control the animation
        if (timelineRef.current) {
          // Get the ScrollTrigger instance
          const st = timelineRef.current.scrollTrigger;

          if (st) {
            // Calculate the scroll position that corresponds to this progress
            const scrollStart = st.start;
            const scrollEnd = st.end;
            const targetScroll =
              scrollStart + (scrollEnd - scrollStart) * value;

            // Scroll to the calculated position
            window.scrollTo({
              top: targetScroll,
              behavior: isMobile ? "auto" : "smooth", // Instant scroll on mobile
            });
          }
        }
      };

      // Add event listeners for range input
      range.addEventListener("input", handleRangeChange);
      range.addEventListener("change", handleRangeChange);

      // Cleanup - remove event listeners (timeline cleanup is handled by useGSAP)
      return () => {
        range.removeEventListener("input", handleRangeChange);
        range.removeEventListener("change", handleRangeChange);
      };
    });
  }, [items.length, yOffset]);

  return (
    <div ref={contentRef}>
      <div
        ref={containerRef}
        className={cn("container relative z-10", containerClassName)}
      >
        {title}
        <div className="flex lg:ml-4 md:ml-0 gap-8 md:gap-16 xl:gap-24">
          <div className="hidden md:block relative flex-shrink-0">
            <input
              ref={rangeRef}
              type="range"
              name="range"
              min="0"
              max="1"
              step="0.001"
              aria-hidden="true"
              tabIndex={-1}
              className="our-technology-progress-bar w-[300px] rotate-90 origin-left absolute pointer-events-none mt-10"
              style={{
                background: rangeBackground,
                height: "4px",
                borderRadius: "4px",
                outline: "none",
                WebkitAppearance: "none",
              }}
            />
          </div>

          <div
            className={cn(
              "flex-1 relative flex flex-col md:overflow-hidden",
              contentClassName
            )}
            style={{
              maskImage: maskGradient,
            }}
          >
            {items.map((item, index) => (
              <p
                key={index}
                ref={(el) => {
                  itemsRef.current[index] = el;
                }}
                className={cn("lg:text-2xl", itemClassName, {
                  "mt-10": index === 0,
                })}
              >
                <span className={titleClassName}>{item.title}</span>:{" "}
                <span className={descriptionClassName}>{item.description}</span>
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
